import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import StickyPageHeader from '../components/common/StickyPageHeader';
import { createPortal } from 'react-dom';
import {
  FileText,
  X,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeftRight,
  Wallet,
  Layers,
  Calendar,
  CalendarDays,
  CalendarRange,
  Settings2,
  SlidersHorizontal
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useLedgerQuery, useAccountsQuery, useCategoriesQuery } from '../hooks/useQueries';
import { LedgerPageSkeleton } from '../components/common/Skeleton';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { useTheme } from '../context/ThemeContext';
import { transactionAPI } from '../services/api';
import { exportToPDF, exportToExcel, checkNotificationPermission, requestNotificationPermission } from '../utils/exportUtils';
import { showPermissionDeniedDialog } from '../services/native';
import Pagination from '../components/common/Pagination';
import MobileSelect from '../components/common/MobileSelect';
import DateTimeInput from '../components/common/DateTimeInput';
import useModalBack from '../hooks/useModalBack';
import { getIconComponent } from '../components/common/IconPicker';

const TYPE_OPTIONS = [
  { value: '', label: 'All Types', icon: Layers, iconColor: '#6b7280' },
  { value: 'income', label: 'Income', icon: ArrowUpRight, iconColor: '#22c55e' },
  { value: 'expense', label: 'Expense', icon: ArrowDownRight, iconColor: '#ef4444' },
  { value: 'transfer', label: 'Transfer', icon: ArrowLeftRight, iconColor: '#6366f1' }
];

const PERIOD_OPTIONS = [
  { value: 'thisMonth', label: 'This Month', icon: CalendarDays, iconColor: '#8b5cf6' },
  { value: 'lastMonth', label: 'Last Month', icon: Calendar, iconColor: '#3b82f6' },
  { value: 'annually', label: 'This Year', icon: CalendarRange, iconColor: '#22c55e' },
  { value: 'custom', label: 'Custom', icon: Settings2, iconColor: '#6b7280' }
];

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getDateRangeFromPeriod(period) {
  const now = new Date();
  let start, end;
  if (period === 'thisMonth') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  } else if (period === 'lastMonth') {
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    end = new Date(now.getFullYear(), now.getMonth(), 0);
  } else if (period === 'annually') {
    start = new Date(now.getFullYear(), 0, 1); // Jan 1
    end = new Date(now.getFullYear(), 11, 31); // Dec 31
  }
  if (start && end) {
    return { startDate: formatLocalDate(start), endDate: formatLocalDate(end) };
  }
  return { startDate: '', endDate: '' };
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDateHeader(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return `${String(d.getDate()).padStart(2, '0')} ${MONTH_SHORT[d.getMonth()].toUpperCase()} ${d.getFullYear()}`;
}

export default function LedgerPage() {
  const { hasEncryptionKey } = useAuth();
  const { formatAmount, formatDate, formatTime, config } = useConfig();
  const { themeColors } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const period = searchParams.get('period') || 'thisMonth';
  const defaultDates = useMemo(() => getDateRangeFromPeriod('thisMonth'), []);
  const startDate = searchParams.get('startDate') || (period === 'thisMonth' ? defaultDates.startDate : '');
  const endDate = searchParams.get('endDate') || (period === 'thisMonth' ? defaultDates.endDate : '');
  const accountFilter = searchParams.get('account') || '';
  const category = searchParams.get('category') || '';
  const type = searchParams.get('type') || '';
  const search = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page') || '1');

  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Filter modal (bottom sheet)
  const [showFilterModal, setShowFilterModal] = useState(false);
  const closeFilterModal = useCallback(() => setShowFilterModal(false), []);
  const openFilterModal = useCallback(() => setShowFilterModal(true), []);
  const { closeSilent: closeSilentFilter } = useModalBack(showFilterModal, closeFilterModal, 'LedgerFilter');

  // Temp filter state
  const [tempPeriod, setTempPeriod] = useState(period);
  const [tempType, setTempType] = useState(type);
  const [tempAccountId, setTempAccountId] = useState(accountFilter);
  const [tempCategory, setTempCategory] = useState(category);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [tempSearchInput, setTempSearchInput] = useState(search);

  useEffect(() => {
    if (showFilterModal) {
      setTempPeriod(period);
      setTempType(type);
      setTempAccountId(accountFilter);
      setTempCategory(category);
      setTempStartDate(startDate);
      setTempEndDate(endDate);
      setTempSearchInput(search);
    }
  }, [showFilterModal, period, type, accountFilter, category, startDate, endDate, search]);

  const handleTempPeriodChange = useCallback((newPeriod) => {
    setTempPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      const { startDate: s, endDate: e } = getDateRangeFromPeriod(newPeriod);
      setTempStartDate(s);
      setTempEndDate(e);
    }
  }, []);

  const isNativePlatform = Capacitor.isNativePlatform();

  const showToast = (message, duration = 3000) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(''), duration);
  };

  const { data: accounts = [] } = useAccountsQuery({}, { enabled: hasEncryptionKey });
  const { data: categories = [] } = useCategoriesQuery({ enabled: hasEncryptionKey });

  // Category icon map
  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach(c => {
      map[c.name] = { icon: getIconComponent(c.icon), color: c.color };
    });
    return map;
  }, [categories]);

  // Account options for filter MobileSelect
  const accountOptions = useMemo(() => {
    return [
      { value: '', label: 'All Accounts', icon: Wallet, iconColor: '#6b7280' },
      ...accounts.filter(a => a.isActive !== false).map(acc => ({
        value: acc.name,
        label: acc.name,
        icon: getIconComponent(acc.icon),
        iconColor: acc.color
      }))
    ];
  }, [accounts]);

  // Account pill list for quick filter
  const activeAccounts = useMemo(() => accounts.filter(a => a.isActive !== false), [accounts]);

  // Category filter options — use _id as value, name as label
  const categoryOptions = useMemo(() => {
    return categories.map(c => c._id).filter(Boolean);
  }, [categories]);
  const categoryNameMap = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c._id] = c.name; });
    return map;
  }, [categories]);

  // Build query params
  const queryParams = useMemo(() => {
    const params = { page, limit: 100 };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (accountFilter) params.accountId = accountFilter;
    if (category) params.category = category;
    if (type) params.type = type;
    if (search) params.search = search;
    return params;
  }, [page, startDate, endDate, accountFilter, category, type, search]);

  const { data: ledgerData, isLoading } = useLedgerQuery(queryParams, {
    enabled: hasEncryptionKey
  });

  const transactions = ledgerData?.transactions || [];
  const summary = ledgerData?.summary || { openingBalance: 0, totalIncome: 0, totalExpense: 0, closingBalance: 0 };
  const pagination = ledgerData?.pagination || { page: 1, pages: 1, total: 0 };

  // Update URL params
  const updateFilter = useCallback((key, value, extraUpdates = {}) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(key, value);
    else newParams.delete(key);
    Object.entries(extraUpdates).forEach(([k, v]) => {
      if (v) newParams.set(k, v);
      else newParams.delete(k);
    });
    if (key !== 'page') newParams.set('page', '1');
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Handle desktop period change
  const handlePeriodChange = useCallback((newPeriod) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('period', newPeriod);
    if (newPeriod !== 'custom') {
      const { startDate: s, endDate: e } = getDateRangeFromPeriod(newPeriod);
      newParams.set('startDate', s);
      newParams.set('endDate', e);
    } else {
      newParams.delete('startDate');
      newParams.delete('endDate');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Clear all filters
  const clearFilters = () => {
    const defaultDates = getDateRangeFromPeriod('thisMonth');
    const newParams = new URLSearchParams();
    newParams.set('period', 'thisMonth');
    newParams.set('startDate', defaultDates.startDate);
    newParams.set('endDate', defaultDates.endDate);
    setSearchParams(newParams);
  };

  const hasActiveFilters = period !== 'thisMonth' || accountFilter || category || type || search;

  // Apply filter from bottom sheet
  const applyFilters = () => {
    const newParams = new URLSearchParams();
    newParams.set('period', tempPeriod);
    if (tempType) newParams.set('type', tempType);
    if (tempAccountId) newParams.set('account', tempAccountId);
    if (tempCategory) newParams.set('category', tempCategory);
    if (tempStartDate) newParams.set('startDate', tempStartDate);
    if (tempEndDate) newParams.set('endDate', tempEndDate);
    if (tempSearchInput) newParams.set('search', tempSearchInput);
    newParams.set('page', '1');
    closeSilentFilter();
    setSearchParams(newParams, { replace: true });
  };

  // Handle account pill click
  const handleAccountPill = (accName) => {
    updateFilter('account', accountFilter === accName ? '' : accName);
  };

  // Export
  const handleExport = async (format) => {
    setShowExportMenu(false);
    let hasNotificationPermission = true;
    if (isNativePlatform) {
      const currentPermission = await checkNotificationPermission();
      if (currentPermission.status !== 'granted') {
        const reqResult = await requestNotificationPermission();
        if (!reqResult.granted) {
          await showPermissionDeniedDialog('notifications');
        }
        hasNotificationPermission = reqResult.granted;
      }
    }
    setIsExporting(true);
    showToast(hasNotificationPermission ? "Generating report... We'll notify you when ready." : "Generating report...");
    try {
      const exportParams = { ...queryParams, page: 1, limit: 10000 };
      const { data } = await transactionAPI.getLedger(exportParams);
      const filters = { startDate, endDate, accountId: accountFilter, category, type };
      const exportConfig = {
        currencySymbol: config.currencySymbol || '₹',
        currencyCode: config.currency || 'INR',
        incomeColor: config.incomeColor || '#10b981',
        expenseColor: config.expenseColor || '#ef4444',
        accentColor: themeColors?.accent?.bg || '#3b82f6'
      };
      if (format === 'pdf') {
        await exportToPDF(data.data.transactions, data.data.summary, filters, formatAmount, formatDate, exportConfig);
      } else {
        await exportToExcel(data.data.transactions, data.data.summary, filters, formatAmount, formatDate, exportConfig);
      }
      if (isNativePlatform && !hasNotificationPermission) {
        showToast('File downloaded! Check your Documents folder.', 4000);
      }
    } catch (error) {
      console.error('[LedgerPage] Export failed:', error);
      showToast('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Group transactions by date for mobile view
  const groupedTransactions = useMemo(() => {
    const groups = [];
    let currentDate = null;
    let currentGroup = null;
    transactions.forEach(tx => {
      const txDate = tx.date ? tx.date.split('T')[0] : '';
      if (txDate !== currentDate) {
        currentDate = txDate;
        currentGroup = { date: txDate, label: formatDateHeader(txDate), transactions: [] };
        groups.push(currentGroup);
      }
      currentGroup.transactions.push(tx);
    });
    return groups;
  }, [transactions]);

  // Filter badge count
  const filterCount = [type, accountFilter, category, period !== 'thisMonth' ? period : null, search].filter(Boolean).length;

  if (isLoading && transactions.length === 0) {
    return <LedgerPageSkeleton />;
  }

  return (
    <div className="space-y-3 pb-24 xl:pb-6">
      {/* Toast */}
      {toastMessage && (
        <div className="fixed top-4 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:w-auto md:max-w-md z-50 animate-fade-in">
          <div className="bg-surface-800 text-white px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 text-sm">
            {isExporting && (
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0" />
            )}
            <span>{toastMessage}</span>
          </div>
        </div>
      )}

      {/* =================== MOBILE =================== */}

      {/* Mobile Header */}
      <StickyPageHeader className="xl:hidden flex items-center justify-between mb-1">
        <h1 className="text-lg font-bold text-surface-900">Ledger</h1>
        <div className="flex items-center gap-2">
          {/* Export */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={isExporting || transactions.length === 0}
              className="w-9 h-9 rounded-xl flex items-center justify-center border bg-white border-surface-200 text-surface-600 disabled:opacity-50 transition-all"
            >
              <span className="text-base">⬇️</span>
            </button>
            {showExportMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowExportMenu(false)} />
                <div className="absolute right-0 top-12 z-50 bg-white rounded-xl shadow-lg border border-surface-200 py-2 min-w-[140px]">
                  <button
                    onClick={() => handleExport('pdf')}
                    className="w-full px-4 py-2.5 text-left text-sm hover:bg-surface-50 flex items-center gap-2"
                  >
                    📄 Export PDF
                  </button>
                  <button
                    onClick={() => handleExport('excel')}
                    className="w-full px-4 py-2.5 text-left text-sm hover:bg-surface-50 flex items-center gap-2"
                  >
                    📊 Export Excel
                  </button>
                </div>
              </>
            )}
          </div>
          {/* Filter */}
          <button
            onClick={openFilterModal}
            className={`relative w-9 h-9 rounded-xl flex items-center justify-center border transition-all ${
              hasActiveFilters
                ? 'bg-primary-50 border-primary-200 text-primary-600'
                : 'bg-white border-surface-200 text-surface-600'
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            {filterCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary-500 text-white text-[9px] font-bold flex items-center justify-center">
                {filterCount}
              </span>
            )}
          </button>
        </div>
      </StickyPageHeader>

      {/* Mobile Summary Cards */}
      {(() => {
        const items = [
          { label: 'Opening', value: formatAmount(summary.openingBalance), color: 'text-surface-900' },
          { label: 'Income', value: formatAmount(summary.totalIncome), style: { color: config.incomeColor } },
          { label: 'Expense', value: formatAmount(summary.totalExpense), style: { color: config.expenseColor } },
          { label: 'Closing', value: summary.closingBalance < 0 ? `-${formatAmount(Math.abs(summary.closingBalance))}` : formatAmount(summary.closingBalance), color: summary.closingBalance < 0 ? 'text-expense-600' : 'text-primary-600' },
        ];
        const maxLen = Math.max(...items.map(i => i.value.length));
        const useTwoRows = maxLen > 10;

        return (
          <div className={`xl:hidden grid gap-2 ${useTwoRows ? 'grid-cols-2' : 'grid-cols-4'}`}>
            {items.map((item) => (
              <div key={item.label} className="bg-white rounded-xl border border-surface-100 px-3 py-2.5 text-center">
                <p className="text-[10px] text-surface-400 mb-0.5">{item.label}</p>
                <p className={`${useTwoRows ? 'text-sm' : 'text-xs'} font-bold ${item.color || ''}`} style={item.style}>
                  {item.value}
                </p>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Mobile Account Pills */}
      <div className="xl:hidden flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <button
          onClick={() => updateFilter('account', '')}
          className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
            !accountFilter
              ? 'text-white shadow-sm'
              : 'bg-white border border-surface-200 text-surface-600'
          }`}
          style={!accountFilter ? { backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' } : {}}
        >
          All
        </button>
        {activeAccounts.map(acc => (
          <button
            key={acc._id}
            onClick={() => handleAccountPill(acc.name)}
            className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
              accountFilter === acc.name
                ? 'text-white shadow-sm'
                : 'bg-white border border-surface-200 text-surface-600'
            }`}
            style={accountFilter === acc.name ? { backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' } : {}}
          >
            {acc.name}
          </button>
        ))}
      </div>

      {/* Mobile Ledger List */}
      <div className="xl:hidden">
        {groupedTransactions.length > 0 ? (
          <>
            {groupedTransactions.map((group) => (
              <div key={group.date}>
                {/* Date Header */}
                <div className="px-1 py-2 bg-surface-50 border-y border-surface-100">
                  <span className="text-[11px] font-bold text-surface-500 tracking-wide">{group.label}</span>
                </div>
                {/* Transactions */}
                {group.transactions.map((tx) => {
                  const cat = categoryMap[tx.category];
                  const CatIcon = cat?.icon;
                  const isNegativeBalance = tx.balance < 0;

                  return (
                    <button
                      key={tx._id}
                      onClick={() => navigate(`/transactions/${tx._id}`)}
                      className="w-full flex items-center gap-3 px-2 py-3 border-b border-surface-100 active:bg-surface-50 transition-colors text-left"
                    >
                      {/* Category Icon */}
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: cat?.color ? `${cat.color}12` : '#f3f4f6' }}
                      >
                        {CatIcon ? (
                          <CatIcon className="w-5 h-5" style={{ color: cat?.color || '#6b7280' }} />
                        ) : (
                          <span className="text-base">
                            {tx.type === 'transfer' ? '🔄' : tx.type === 'income' ? '💰' : '🧾'}
                          </span>
                        )}
                      </div>
                      {/* Description + Account */}
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-surface-900 truncate leading-tight">
                          {tx.description || (tx.type === 'transfer' ? `${tx.bankName} → ${tx.toAccount}` : tx.category || '—')}
                        </p>
                        <p className="text-[11px] text-surface-400 truncate leading-tight mt-0.5">
                          {tx.bankName}{tx.type === 'transfer' ? ` → ${tx.toAccount}` : ''}
                        </p>
                      </div>
                      {/* Amount Column — stacked: debit/credit then balance */}
                      <div className="flex-shrink-0 text-right">
                        {tx.debit > 0 ? (
                          <p className="text-[13px] font-bold" style={{ color: config.expenseColor }}>
                            {formatAmount(tx.debit)}
                          </p>
                        ) : tx.credit > 0 ? (
                          <p className="text-[13px] font-bold" style={{ color: config.incomeColor }}>
                            {formatAmount(tx.credit)}
                          </p>
                        ) : (
                          <p className="text-[13px] font-bold text-surface-400">—</p>
                        )}
                        <p className={`text-[11px] mt-0.5 ${isNegativeBalance ? 'text-expense-500' : 'text-surface-400'}`}>
                          Bal: {isNegativeBalance ? `-${formatAmount(Math.abs(tx.balance))}` : formatAmount(tx.balance)}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}

            {/* Opening Balance Row (at the bottom = oldest) */}
            {page === pagination.pages && (
              <div className="flex items-center gap-3 px-2 py-3 bg-surface-50 border-b border-surface-100">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-surface-200">
                  <span className="text-base">📖</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-surface-700">Opening Balance</p>
                  <p className="text-[11px] text-surface-400">{startDate ? formatDate(startDate) : ''}</p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-[13px] font-bold text-surface-900">{formatAmount(summary.openingBalance)}</p>
                </div>
              </div>
            )}

            {/* Closing Balance - Sticky Footer (theme-colored) */}
            <div
              className="sticky bottom-16 xl:bottom-0 flex items-center gap-3 px-4 py-3 rounded-xl mt-2 shadow-lg"
              style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
            >
              <span className="text-base">📋</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold opacity-90">Closing Balance</p>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-[10px] opacity-70">
                    Debit: <span className="font-semibold">{formatAmount(summary.totalExpense)}</span>
                  </span>
                  <span className="text-[10px] opacity-70">
                    Credit: <span className="font-semibold">{formatAmount(summary.totalIncome)}</span>
                  </span>
                </div>
              </div>
              <p className="text-base font-extrabold flex-shrink-0">
                {summary.closingBalance < 0 ? `-${formatAmount(Math.abs(summary.closingBalance))}` : formatAmount(summary.closingBalance)}
              </p>
            </div>
          </>
        ) : (
          <div className="bg-white rounded-2xl border border-surface-100 p-10 text-center mt-4">
            <div className="w-14 h-14 bg-surface-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl">📒</span>
            </div>
            <p className="text-surface-900 font-medium">No transactions found</p>
            <p className="text-surface-400 text-sm mt-1">
              {hasActiveFilters ? 'Try adjusting your filters' : 'Start adding transactions to see your ledger'}
            </p>
          </div>
        )}
      </div>

      {/* Mobile Filter Bottom Sheet */}
      {showFilterModal && createPortal(
        <div className="xl:hidden fixed inset-0 z-50 flex items-end !mt-0" onClick={closeFilterModal}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full bg-white rounded-t-2xl animate-slide-up max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-surface-300" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3">
              <h2 className="text-[15px] font-bold text-surface-900">Filter Ledger</h2>
              <div className="flex items-center gap-2">
                {(tempType || tempAccountId || tempCategory || tempPeriod !== 'thisMonth' || tempSearchInput) && (
                  <button
                    onClick={() => {
                      handleTempPeriodChange('thisMonth');
                      setTempType('');
                      setTempAccountId('');
                      setTempCategory('');
                      setTempSearchInput('');
                    }}
                    className="text-xs font-semibold px-2 py-1"
                    style={{ color: 'var(--accent-bg)' }}
                  >
                    Clear All
                  </button>
                )}
                <button
                  onClick={closeFilterModal}
                  className="w-8 h-8 rounded-lg bg-surface-100 flex items-center justify-center text-surface-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto px-4 pb-6 space-y-4">
              {/* Search */}
              <div>
                <label className="block text-[11px] font-semibold text-surface-400 uppercase tracking-wider mb-2">Search</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">🔍</span>
                  <input
                    type="text"
                    value={tempSearchInput}
                    onChange={(e) => setTempSearchInput(e.target.value)}
                    placeholder="Search by description..."
                    className="w-full h-11 pl-10 pr-4 text-sm bg-white border border-surface-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  {tempSearchInput && (
                    <button
                      type="button"
                      onClick={() => setTempSearchInput('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-surface-400"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Time Period */}
              <div>
                <label className="block text-[11px] font-semibold text-surface-400 uppercase tracking-wider mb-2">Time Period</label>
                <div className="grid grid-cols-2 gap-2">
                  {PERIOD_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = tempPeriod === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleTempPeriodChange(option.value)}
                        className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-surface-200 bg-white hover:border-surface-300'
                        }`}
                      >
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${option.iconColor}15` }}
                        >
                          <Icon className="w-3.5 h-3.5" style={{ color: option.iconColor }} />
                        </div>
                        <span className={`text-sm font-medium ${isSelected ? 'text-primary-700' : 'text-surface-600'}`}>
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Date Range */}
              {tempPeriod === 'custom' && (
                <div>
                  <div className="flex gap-2 items-center">
                    <div className="flex-1">
                      <DateTimeInput
                        dateOnly
                        value={tempStartDate}
                        onChange={(date) => setTempStartDate(date)}
                        placeholder="From"
                        maxDate={tempEndDate || formatLocalDate(new Date())}
                      />
                    </div>
                    <span className="text-surface-300 text-sm">&rarr;</span>
                    <div className="flex-1">
                      <DateTimeInput
                        dateOnly
                        value={tempEndDate}
                        onChange={(date) => setTempEndDate(date)}
                        placeholder="To"
                        minDate={tempStartDate || undefined}
                        maxDate={formatLocalDate(new Date())}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Transaction Type */}
              <div>
                <label className="block text-[11px] font-semibold text-surface-400 uppercase tracking-wider mb-2">Transaction Type</label>
                <div className="grid grid-cols-4 gap-2">
                  {TYPE_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = tempType === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => setTempType(option.value)}
                        className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-surface-200 bg-white hover:border-surface-300'
                        }`}
                      >
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${option.iconColor}15` }}
                        >
                          <Icon className="w-3.5 h-3.5" style={{ color: option.iconColor }} />
                        </div>
                        <span className={`text-[11px] font-medium ${isSelected ? 'text-primary-700' : 'text-surface-600'}`}>
                          {option.label.replace('All Types', 'All')}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Account */}
              <div>
                <label className="block text-[11px] font-semibold text-surface-400 uppercase tracking-wider mb-2">Account</label>
                <MobileSelect
                  value={tempAccountId}
                  onChange={(value) => setTempAccountId(value)}
                  options={accountOptions}
                  placeholder="All Accounts"
                  label="Select Account"
                  columns={2}
                />
              </div>

              {/* Apply Button */}
              <button
                onClick={applyFilters}
                className="w-full py-3.5 rounded-xl font-semibold text-white transition-all shadow-lg"
                style={{ backgroundColor: 'var(--accent-bg)' }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* =================== DESKTOP =================== */}

      {/* Desktop Header */}
      <div className="hidden xl:flex items-center justify-between">
        <div className="page-header mb-0">
          <h1 className="page-title">Ledger</h1>
          <p className="page-subtitle">View and export your transaction history</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleExport('excel')}
            disabled={isExporting || transactions.length === 0}
            className="btn-secondary flex items-center gap-2 px-4 py-2"
          >
            <FileText className="w-4 h-4" />
            Excel
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={isExporting || transactions.length === 0}
            className="btn-primary flex items-center gap-2 px-4 py-2"
          >
            📥 {isExporting ? 'Exporting...' : 'PDF'}
          </button>
        </div>
      </div>

      {/* Desktop Summary Cards */}
      <div className="hidden xl:grid xl:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 rounded-lg bg-surface-100">
              <span className="text-sm">📖</span>
            </div>
            <span className="text-sm text-surface-500">Opening</span>
          </div>
          <p className="text-xl font-bold text-surface-900">
            {formatAmount(summary.openingBalance)}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 rounded-lg bg-income-50">
              <span className="text-sm">📈</span>
            </div>
            <span className="text-sm text-surface-500">Income</span>
          </div>
          <p className="text-xl font-bold" style={{ color: config.incomeColor }}>
            {formatAmount(summary.totalIncome)}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 rounded-lg bg-expense-50">
              <span className="text-sm">📉</span>
            </div>
            <span className="text-sm text-surface-500">Expense</span>
          </div>
          <p className="text-xl font-bold" style={{ color: config.expenseColor }}>
            {formatAmount(summary.totalExpense)}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 rounded-lg bg-primary-50">
              <span className="text-sm">💰</span>
            </div>
            <span className="text-sm text-surface-500">Closing</span>
          </div>
          <p className={`text-xl font-bold ${summary.closingBalance < 0 ? 'text-expense-600' : 'text-primary-600'}`}>
            {summary.closingBalance < 0 ? `-${formatAmount(Math.abs(summary.closingBalance))}` : formatAmount(summary.closingBalance)}
          </p>
        </div>
      </div>

      {/* Desktop Filters */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 p-4">
        <div className="flex gap-3 items-center">
          <form onSubmit={(e) => { e.preventDefault(); updateFilter('search', document.getElementById('ledger-search').value); }} className="relative flex-1 min-w-[200px] max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">🔍</span>
            <input
              id="ledger-search"
              type="text"
              placeholder="Search transactions..."
              defaultValue={search}
              className="w-full pl-9 pr-4 py-2 text-sm bg-surface-50 border border-surface-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </form>
          <select
            value={period}
            onChange={(e) => handlePeriodChange(e.target.value)}
            className="select text-sm bg-surface-50 border border-surface-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 w-auto flex-shrink-0"
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {period === 'custom' && (
            <>
              <div className="min-w-[120px]">
                <DateTimeInput
                  dateOnly
                  value={startDate}
                  onChange={(date) => {
                    if (endDate && date > endDate) {
                      updateFilter('startDate', date, { endDate: '' });
                    } else {
                      updateFilter('startDate', date);
                    }
                  }}
                  placeholder="From"
                />
              </div>
              <span className="text-surface-400 text-sm flex-shrink-0">→</span>
              <div className="min-w-[120px]">
                <DateTimeInput
                  dateOnly
                  value={endDate}
                  onChange={(date) => updateFilter('endDate', date)}
                  placeholder="To"
                  minDate={startDate || undefined}
                />
              </div>
            </>
          )}
          <select
            value={accountFilter}
            onChange={(e) => updateFilter('account', e.target.value)}
            className="select text-sm bg-surface-50 border border-surface-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 w-auto flex-shrink-0"
          >
            <option value="">All Accounts</option>
            {activeAccounts.map(acc => (
              <option key={acc._id} value={acc.name}>{acc.name}</option>
            ))}
          </select>
          <select
            value={type}
            onChange={(e) => updateFilter('type', e.target.value)}
            className="select text-sm bg-surface-50 border border-surface-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 w-auto flex-shrink-0"
          >
            {TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-sm text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-xl transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-surface-50 border-b border-surface-200">
                <th className="px-4 py-3 text-left text-xs font-semibold text-surface-600 uppercase tracking-wider w-28">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-surface-600 uppercase tracking-wider">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-surface-600 uppercase tracking-wider w-32">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-surface-600 uppercase tracking-wider w-28">Account</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-surface-600 uppercase tracking-wider w-28">Debit</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-surface-600 uppercase tracking-wider w-28">Credit</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-surface-600 uppercase tracking-wider w-32">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-100">
              {page === 1 && (
                <tr className="bg-surface-50">
                  <td className="px-4 py-3 text-sm text-surface-600">
                    {startDate ? formatDate(startDate) : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-surface-900" colSpan={3}>
                    Opening Balance
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-surface-400">—</td>
                  <td className="px-4 py-3 text-sm text-right text-surface-400">—</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-surface-900">
                    {formatAmount(summary.openingBalance)}
                  </td>
                </tr>
              )}
              {transactions.map((tx) => {
                const cat = categoryMap[tx.category];
                const CatIcon = cat?.icon;
                const isNegativeBalance = tx.balance < 0;
                return (
                  <tr
                    key={tx._id}
                    onClick={() => navigate(`/transactions/${tx._id}`)}
                    className="hover:bg-surface-50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 text-sm text-surface-600">
                      <div>{formatDate(tx.date)}</div>
                      {tx.time && <div className="text-xs text-surface-400">{formatTime(tx.time)}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: cat?.color ? `${cat.color}12` : '#f3f4f6' }}
                        >
                          {CatIcon ? (
                            <CatIcon className="w-3.5 h-3.5" style={{ color: cat?.color || '#6b7280' }} />
                          ) : (
                            <span className="text-xs">
                              {tx.type === 'transfer' ? '🔄' : tx.type === 'income' ? '💰' : '🧾'}
                            </span>
                          )}
                        </div>
                        <span className="text-sm font-medium text-surface-900 truncate max-w-[250px]">
                          {tx.description || (tx.type === 'transfer' ? `${tx.bankName} → ${tx.toAccount}` : '—')}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-surface-600">{tx.category || '—'}</td>
                    <td className="px-4 py-3 text-sm text-surface-600">
                      {tx.type === 'transfer' ? (
                        <span className="text-xs">{tx.bankName} → {tx.toAccount}</span>
                      ) : tx.bankName}
                    </td>
                    <td className="px-4 py-3 text-sm text-right" style={{ color: tx.debit > 0 ? config.expenseColor : 'inherit' }}>
                      {tx.debit > 0 ? formatAmount(tx.debit) : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-right" style={{ color: tx.credit > 0 ? config.incomeColor : 'inherit' }}>
                      {tx.credit > 0 ? formatAmount(tx.credit) : '—'}
                    </td>
                    <td className={`px-4 py-3 text-sm text-right font-semibold ${isNegativeBalance ? 'text-expense-600' : 'text-surface-900'}`}>
                      {isNegativeBalance ? `-${formatAmount(Math.abs(tx.balance))}` : formatAmount(tx.balance)}
                    </td>
                  </tr>
                );
              })}
              {transactions.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <span className="text-4xl block mb-4">📒</span>
                    <h3 className="text-lg font-semibold text-surface-900 mb-1">No transactions found</h3>
                    <p className="text-sm text-surface-500">
                      {hasActiveFilters ? 'Try adjusting your filters' : 'Start adding transactions to see your ledger'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.pages}
          totalItems={pagination.total}
          itemsPerPage={100}
          onPageChange={(newPage) => updateFilter('page', newPage.toString())}
        />
      )}
    </div>
  );
}
